import { FILTER_FEATURE, PERMISSION_DATA } from "api/ApiPath";
import { UserFeatureResponseModel } from "api/merchantRepository/features/model/UserFeatureResponseModel";
import { BasePermissionCapability } from "app/based/baseApiModel/basePermissionMenu/BasePermissionCapability";
import { BasePermissionMenu } from "app/based/baseApiModel/basePermissionMenu/BasePermissionMenu";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { AppStartUpInjection } from "app/startup/di/AppStartUpInjection";
import { SidebarMenuInjection } from "components/sidebar/di/SidebarMenuInjection";
import { LoginResponseModel } from "features/login/data/model/responseModel/LoginResponseModel";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { UserRoleEnum } from "utils/enums/UserRoleEnum";
import { AppStartUpEntity } from "../entities/AppStartUpEntity";
import { AppStartUpRequestEntity } from "../entities/AppStartUpRequestEntity";
import { AppStartUpResponseEntity } from "../entities/AppStartUpResponseEntity";
import { IAppStartUpRepository } from "../repositories/IAppStartUpRepository";
import { AppBasedServiceInjection } from "utils/serviceRepo/ServiceRepo";

@injectable()
export class AppStartUpUseCase {
  private appStartUpRepo: IAppStartUpRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.appStartUpRepo = AppStartUpInjection.getRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  private normalizePathname(pathname?: string) {
    return pathname === "/" || pathname === "" || isUndefined(pathname)
      ? "/dashboard"
      : pathname;
  }

  private cloneValue<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  private appendAdminVnskyMenus(
    menus: BasePermissionMenu[] = [],
    userInfo?: LoginResponseModel,
  ) {
    if (!this.isAdminRole(userInfo)) {
      return menus || [];
    }

    const clonedMenus = this.cloneValue(menus || []);

    return [...clonedMenus];
  }

  private findMenuByPath(menus: any[] = [], pathname?: string): any {
    const normalizedPath = this.normalizePathname(pathname);
    if (!Array.isArray(menus) || !normalizedPath) {
      return null;
    }

    for (const menu of menus) {
      const isHasPathName = normalizedPath?.includes(menu?.path);
      if (!menu?.sub?.length && isHasPathName) {
        return menu;
      }

      if (menu?.sub?.length) {
        const found = this.findMenuByPath(menu.sub, normalizedPath);
        if (found) return found;
      }
    }

    return null;
  }

  private syncCapabilitiesByRoute(
    input: AppStartUpRequestEntity,
    result: AppStartUpResponseEntity,
  ) {
    const matchedMenu = this.findMenuByPath(
      result?.menuConfig?.menu ||
        result?.userMenu ||
        productCache?.getCache(CacheKeyEnum.userMenu) ||
        [],
      input?.pathname,
    );
    const capabilities = (matchedMenu?.capabilities ||
      []) as BasePermissionCapability[];

    productCache?.saveCache({
      key: CacheKeyEnum.capabilities,
      product: capabilities,
      needExpired: false,
    });
    result.capabilities = capabilities;
  }

  private isAdminRole(userInfo?: LoginResponseModel) {
    return [
      UserRoleEnum.sAdmin,
      UserRoleEnum.admin,
      UserRoleEnum.accountant,
    ]?.includes(userInfo?.role?.code as UserRoleEnum);
  }

  private mapFeaturesFromMenu(
    menus: BasePermissionMenu[] = [],
  ): UserFeatureResponseModel[] {
    const featureMap = new Map<string, UserFeatureResponseModel>();

    const mappedMenus = (items: BasePermissionMenu[] = []) => {
      items.forEach((menu) => {
        (menu?.capabilities || []).forEach((capability) => {
          const feature = capability?.feature;
          const featureKey = feature?.code || feature?.path;

          if (featureKey) {
            featureMap.set(featureKey, {
              name: feature?.name,
              code: feature?.code,
              path: feature?.path,
            });
          }
        });

        if (!isEmpty(menu?.sub)) {
          mappedMenus(menu?.sub || []);
        }
      });
    };

    mappedMenus(menus);
    return Array.from(featureMap.values());
  }

  private buildTasks(
    userInfo: LoginResponseModel,
    input: AppStartUpRequestEntity,
    result: AppStartUpResponseEntity,
  ): AppStartUpEntity[] {
    return [
      {
        key: "load-user",
        priority: "critical",
        run: async () => {
          result.user = userInfo;
        },
      },
      {
        key: "load-user-menu",
        priority: "critical",
        run: async () => {
          const cache = productCache.getCache(CacheKeyEnum.userMenu);
          if (cache && !input?.isForceRefresh && !input.isUserReload) {
            result.userMenu = this.appendAdminVnskyMenus(cache, userInfo);
            return;
          }
          const res = await this.filterUseCase.fetchListWithFeature({
            path: PERMISSION_DATA.USER_MENU,
            isForceRefresh: !!input?.isForceRefresh || !!input?.isUserReload,
            needExpired: false,
            isNeedFeature: false,
            params: {},
            savedKey: CacheKeyEnum.userMenu,
          });
          result.userMenu = this.appendAdminVnskyMenus(
            (res as Array<BasePermissionMenu>) || [],
            userInfo,
          );
        },
      },
      {
        key: "load-feature",
        priority: "critical",
        dependsOn: ["load-user", "load-user-menu"],
        run: async () => {
          const cache = productCache.getCache(CacheKeyEnum.internalFeature);
          if (cache && !input?.isForceRefresh && !input.isUserReload) {
            result.userFeature = cache;
            return;
          }

          if (this.isAdminRole(result?.user)) {
            const repo = AppBasedServiceInjection.getFeatureRepo();
            const res = await repo.getUserFeature();
            if (res?.data) {
              productCache.saveCache({
                key: CacheKeyEnum.internalFeature,
                product: res.data,
                needExpired: false,
              });
              result.userFeature = res.data;
            }
            return;
          } else {
            const mappedFeatures = this.mapFeaturesFromMenu(result?.userMenu);
            if (!isEmpty(mappedFeatures)) {
              productCache.saveCache({
                key: CacheKeyEnum.internalFeature,
                product: mappedFeatures,
                needExpired: false,
              });
              result.userFeature = mappedFeatures;
            }
          }
        },
      },
      {
        key: "build-sidebar",
        priority: "critical",
        dependsOn: ["load-feature", "load-user-menu"],
        shouldSkip: () => input?.needBuildSidebar === false,
        run: async () => {
          const sidebarUseCase = SidebarMenuInjection.getUseCase();
          const sidebar = await sidebarUseCase.getSidebarMenuFromSource(
            (result.userMenu as any[]) || [],
          );
          if (sidebar?.data) {
            result.menuConfig = sidebar?.data;
          }
        },
      },
      {
        key: "sync-capabilities",
        priority: "critical",
        dependsOn: ["load-user-menu"],
        run: async () => {
          this.syncCapabilitiesByRoute(input, result);
        },
      },
      {
        key: "load-overview-services",
        priority: "background",
        run: async () => {
          const cache = productCache.getCache(CacheKeyEnum.serviceList);
          if (cache && !input?.isForceRefresh) {
            result.serviceOptions = cache;
            return;
          } else {
            const res = await this.filterUseCase.fetchListWithFeature({
              path: FILTER_FEATURE.SERVICE_LIST,
              params: {},
              isForceRefresh: input?.isForceRefresh,
              isNeedFeature: true,
              savedKey: CacheKeyEnum.serviceList,
              needExpired: false,
            });
            if (res) {
              result.serviceOptions = res;
            }
          }
        },
      },
    ];
  }

  async runTasks(
    input: AppStartUpRequestEntity,
  ): Promise<AppStartUpResponseEntity> {
    const result = new AppStartUpResponseEntity();
    const userInfo = input?.user || productCache?.getCache(CacheKeyEnum.user);

    const tasks = this.buildTasks(userInfo, input, result);
    const taskKeys = Array.from(
      new Set(
        (input?.taskKeys?.filter(Boolean) || []).flatMap((item) =>
          ["load-user-menu", "build-sidebar"].includes(item)
            ? [item, "sync-capabilities"]
            : [item],
        ),
      ),
    );

    if (!!taskKeys?.length) {
      await this.appStartUpRepo.runByKeys(tasks, taskKeys, "critical");
      await this.appStartUpRepo.runByKeys(tasks, taskKeys, "important");
      void this.appStartUpRepo
        .runByKeys(tasks, taskKeys, "background")
        .then(async () => {
          await input?.onBackgroundCompleted?.(
            new AppStartUpResponseEntity({ ...result }),
          );
        });
    } else {
      await this.appStartUpRepo.run(tasks, "critical");
      await this.appStartUpRepo.run(tasks, "important");
      void this.appStartUpRepo.run(tasks, "background").then(async () => {
        await input?.onBackgroundCompleted?.(
          new AppStartUpResponseEntity({ ...result }),
        );
      });
    }

    return result;
  }

  resetTasks() {
    this.appStartUpRepo.reset();
  }
}
